import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'next-i18next';
import { create, InstanceProps } from 'react-modal-promise';
import { SubmitHandler, useForm } from 'react-hook-form';
import { isAxiosError } from 'axios';

import Modal from 'components/Modal';
import IconButton from 'components/IconButton';
import Tooltip from 'components/Tooltip';
import Confirmation from 'components/Confirmation';
import { ButtonV3, InputV2 } from 'components/ComponentV2';
import { useNotification } from 'hooks/useNotification';
import { useResponsive } from 'hooks/useResponsive';
import { useGlobalSearch } from 'lib/providers/GlobalSearchProvider';
import { TSearchList } from 'lib/models/search-page';

import styles from './ManageSavedLists.module.scss';

export const ManageSavedLists = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [t] = useTranslation('search.results');
  const screens = useResponsive();
  const notificationInstance = useNotification();
  const { searchLists, handleDeleteList, handleUpdateList } = useGlobalSearch();

  const onToggleListAlert = async ({ listId, alert }: { listId: string; alert: boolean }) => {
    try {
      await handleUpdateList({ listId, data: { alert: !alert } });
      if (alert) {
        notificationInstance.information({
          title: t('saved-lists.alert-disabled'),
          message: '',
          icon: 'icon_check-double',
          variant: screens.sm ? 'micro' : 'small',
          showClose: false,
        });
      } else {
        notificationInstance.information({
          title: t('saved-lists.alert-enabled'),
          message: '',
          icon: 'icon_check-double',
          variant: screens.sm ? 'micro' : 'small',
          showClose: false,
        });
      }
    } catch (error) {
      console.error('Error toggling list alert', error);
    }
  };

  const onDeleteList = useCallback(
    async (listId: string) => {
      try {
        await handleDeleteList({ listId });
        notificationInstance.information({
          title: t('saved-lists.deleted'),
          message: '',
          icon: 'icon_check-double',
          variant: screens.sm ? 'micro' : 'small',
          showClose: false,
        });
        if (searchLists.length <= 1) {
          onClose();
        }
      } catch (error) {
        console.error('Error deleting list', error);
      }
    },
    [handleDeleteList, searchLists, notificationInstance, screens.sm]
  );

  const handleDelete = useCallback(
    async (list: TSearchList) => {
      try {
        await deleteConfirmationModalInstance({ listName: list.name });
        await onDeleteList(list.id);
      } catch (e) {
        console.error('Delete cancelled');
      }
    },
    [onDeleteList]
  );

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} contentClassName={styles.container}>
      <header className={styles.header}>
        <p className={styles.title}>{t('saved-lists.modal.title')}</p>
        <IconButton iconName="close" onClick={onClose} className={styles['close-icon']} size="small" />
      </header>
      <main className={styles.main}>
        {searchLists.length > 0 ? (
          <ul>
            {searchLists.map((list) => {
              const { id: listId, name: listName, alert } = list;
              return (
                <li className={styles['list']} key={listId}>
                  <p className={styles.name}>{listName}</p>
                  <div className={styles.actions}>
                    <Tooltip
                      content={alert ? t('saved-lists.modal.disable-alerts') : t('saved-lists.modal.enable-alerts')}
                      position="top"
                      color="dark"
                      disabled={screens.sm}
                    >
                      <IconButton
                        iconName={alert ? 'icon_notification-bell' : 'icon_notification-bell-disabled'}
                        className={styles['action-icon']}
                        onClick={() => onToggleListAlert({ listId, alert })}
                        size={screens.sm ? 'xmediumplus' : 'medium'}
                      />
                    </Tooltip>
                    <Tooltip content={t('saved-lists.modal.delete')} position="top" color="dark" disabled={screens.sm}>
                      <IconButton
                        size={screens.sm ? 'xmediumplus' : 'medium'}
                        iconName="icon_trash"
                        className={styles['action-icon']}
                        onClick={() => handleDelete(list)}
                      />
                    </Tooltip>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className={styles['no-lists']}>{t('saved-lists.modal.no-lists')}</p>
        )}
      </main>
      <footer className={styles.footer}>
        <ButtonV3 onClick={onClose} isFullWidth={screens.sm}>
          {t('saved-lists.modal.close')}
        </ButtonV3>
      </footer>
    </Modal>
  );
};

type DeleteConfirmationModalInstanceProps = InstanceProps<boolean> & {
  listName: string;
};

const DeleteConfirmationModalInstance: React.FC<DeleteConfirmationModalInstanceProps> = ({ isOpen, onResolve, onReject, listName }) => {
  const [t] = useTranslation('search.results');

  return (
    <Modal isOpen={isOpen} onRequestClose={onReject} isFullScreen={false}>
      <Confirmation
        title={t('saved-lists.delete-modal.title')}
        desc={
          <Trans t={t} i18nKey="saved-lists.delete-modal.description" values={{ listName }}>
            1 <span className={styles.bold}>2</span>
          </Trans>
        }
        buttonContent={t('saved-lists.delete-modal.delete')}
        onCancel={onReject}
        onConfirm={onResolve}
        isPrimary={false}
        secondaryActionOnRight={true}
      />
    </Modal>
  );
};

export const deleteConfirmationModalInstance = create(DeleteConfirmationModalInstance);

type ListNameModalInstanceProps = InstanceProps<boolean> & { onSubmit: ({ listName }: { listName: string }) => Promise<void> };

const ListNameModalInstance: React.FC<ListNameModalInstanceProps> = ({ isOpen, onResolve, onReject, onSubmit }) => {
  const { handleAddList } = useGlobalSearch();
  const { sm } = useResponsive();
  const [t] = useTranslation('search.results');
  const [tc] = useTranslation('common');
  const [serverError, setServerError] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  const onCancel = () => onReject();

  const onFormSubmit: SubmitHandler<{ listName: string }> = useCallback(
    async (data) => {
      try {
        await onSubmit(data);
        onResolve();
      } catch (error) {
        if (isAxiosError(error)) {
          setServerError(error.response?.data?.alert);
        }
      }
    },
    [handleAddList]
  );

  return (
    <Modal isOpen={isOpen} onRequestClose={onReject} isFullScreen={false}>
      <div className={styles.container}>
        <div className={styles.header}>
          <p className={styles.title}>{t('saved-lists.list-name-modal.title')}</p>
          <IconButton className={styles['close-icon']} size="small" iconName="close" onClick={onCancel} />
        </div>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <div className={styles.main}>
            <InputV2
              label={t('saved-lists.list-name-modal.field-name')}
              className={styles.input}
              placeholder={t('saved-lists.list-name-modal.placeholder')}
              autoFocus={true}
              {...register('listName', {
                required: tc('required'),
                maxLength: {
                  value: 100,
                  message: t('saved-lists.list-name-modal.max-length-error'),
                },
              })}
              isInvalid={!!errors.listName}
              helperText={errors.listName?.message}
            />
            {serverError && <p className={styles['server-error']}>{serverError}</p>}
          </div>
          <div className={styles['form-footer']}>
            <ButtonV3 borderRadius={10} type="reset" color="secondary" onClick={onCancel} className={styles['cancel-btn']}>
              {t('saved-lists.list-name-modal.cancel')}
            </ButtonV3>
            <ButtonV3 color="primary" borderRadius={10} type="submit" isLoading={isSubmitting} isFullWidth={sm} size={sm ? 'large' : 'medium'}>
              {t('saved-lists.list-name-modal.submit')}
            </ButtonV3>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export const listNameModalInstance = create(ListNameModalInstance);
